<script lang="ts">
  import { onMount } from "svelte"
  import Button from "$app/components/ButtonSpinner.svelte"
  import RecognitionComment from "./RecognitionComment.svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import Tiptap from "$app/components/Tiptap.svelte"
  import { FilePenLine, Trash2, MessageSquareText } from "lucide-svelte"
  import { xhr, timeAgoOrDate, queryToUrl, toast } from "$app/utils"
  import { emojis } from "$app/constants"
  import { Editor } from "@tiptap/core"
  import RecognitionDialog from "$app/Pages/Plans/Recognitions/RecognitionDialog.svelte"
  import Modal from "$app/components/Modal.svelte"
  import Reaction from "$app/components/Reaction.svelte"
  import { appStore } from "$app/store.svelte"

  interface Props {
    recognition: RecognitionType
    readOnly?: boolean
    showComments?: boolean
    ondelete?: (recognition: RecognitionType) => void
  }

  let {
    recognition = $bindable(),
    readOnly = false,
    showComments = $bindable(false),
    ondelete = () => {}
  }: Props = $props()

  let openEditModal = $state(false)
  let tiptapEditor: Editor = $state()

  let recipients: (UserType | ExternalRecipientType)[] = $state(
    recognition.recipients
  )

  if (recognition.external_recipients) {
    recipients = [...recognition.recipients, ...recognition.external_recipients]
  }

  // Create comment
  let showCommentBtn = $state(false)
  let newComment = $state("")
  let savingComment = $state(false)
  async function createComment(): Promise<void> {
    savingComment = true

    try {
      const resp: Response<any, RecognitionCommentType> = await xhr.post(
        `/plans/${recognition.plan_id}/recognition_comments.json`,
        {
          recognition_comment: {
            recognition_id: recognition.id,
            body: newComment
          }
        }
      )
      savingComment = false
      tiptapEditor.commands.clearContent()

      recognition.comments = [resp.data, ...recognition.comments]
      recognition = recognition
    } catch (e: any) {
      Honeybadger.notify(e)
    } finally {
      newComment = ""
      showCommentBtn = false
    }
  }

  const hasEmojiReactions = function (emoji: string) {
    return recognition.reactions.filter((r) => r.emoji === emoji).length > 0
  }

  const reactionsByEmoji = function (emoji: string) {
    return recognition.reactions.filter((r) => r.emoji === emoji)
  }

  const deleteComment = async function (comment: RecognitionCommentType) {
    const idx = recognition.comments.findIndex((c) => c.id === comment.id)
    recognition.comments[idx]._selected = true

    setTimeout(async () => {
      if (confirm("Are you sure you want to delete this comment?")) {
        try {
          await xhr.delete(
            `/plans/${recognition.plan_id}/recognition_comments/${comment.id}`
          )

          recognition.comments = recognition.comments.filter(
            (c) => c.id !== comment.id
          )
        } catch (e: any) {
          if (typeof Honeybadger.notify !== "undefined") {
            Honeybadger.notify(e)
          }
          toast("There was an error deleting the comment", "error")
        }
      } else {
        recognition.comments[idx]._selected = false
        recognition.comments = recognition.comments
      }
    })
  }

  function updateReaction(reaction: ReactionType) {
    currentReaction = reaction
    recognition.reactions = recognition.reactions.map((r) => {
      if (r.id === reaction.id) {
        return { ...r, ...reaction }
      }

      return r
    })
    recognition = recognition
  }

  function removeReaction(reaction: ReactionType) {
    recognition.reactions = recognition.reactions.filter(
      (r) => r.id !== reaction.id
    )

    currentReaction = { id: null, emoji: null } as ReactionType

    recognition = recognition
  }

  let currentReaction: ReactionType = $state(
    recognition.reactions.find((r) => r.user_id === appStore.current_user.id) ||
      ({ id: null, emoji: null } as ReactionType)
  )

  let loadedCommentReactions = false
  async function loadCommentReactions() {
    const ids = recognition.comments.map((c) => c.id)

    const query = queryToUrl({
      reactionable_type: "RecognitionComment",
      reactionable_id: ids
    })

    try {
      const resp = await xhr.get(
        `/api/v1/plans/${recognition.plan_id}/reactions.json?${query}`
      )

      recognition.comments = recognition.comments.map((c) => {
        c.reactions = resp.data.filter((r) => r.reactionable_id === c.id)
        return c
      })

      loadedCommentReactions = true
    } catch (e: any) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
    }
  }

  onMount(() => {
    if (showComments) {
      loadCommentReactions()
    }
  })
</script>

<li
  data-name="recognition"
  class="bg-white rounded-sm shadow mb-3 {recognition._selected
    ? 'border-2 border-red-200 bg-gray-100'
    : ''}"
>
  <div class="w-full flex space-x-2 lg:space-x-5 p-2 lg:p-6">
    <div class="flex-shrink-0 px-4 lg:px-0">
      <div class="my-4 flex-shrink-0 mt-0 lg:w-40 pr-4 sm:pr-2">
        <div class="flex overflow-visible -space-x-4 flex-wrap justify-start">
          {#each recipients as recipient (recipient.id)}
            <img
              class="inline-block h-8 w-8 md:h-12 md:w-12 rounded-full ring-2 ring-white"
              src={recipient.avatar_thumb_url}
              alt={recipient.name}
            />
          {/each}
        </div>
      </div>
      <p class="text-xs md:text-sm text-gray-500 text-left">
        {timeAgoOrDate(recognition.created_at, 2)}
      </p>
    </div>
    <div class="mt-3 lg:mt-0 pt-1 text-left flex-grow relative">
      {#if appStore.current_user.id === recognition.sender_user_id && !readOnly}
        <div class="absolute right-0 top-0">
          <button onclick={() => (openEditModal = true)}>
            <FilePenLine class="w-4 h-4 text-gray-400 hover:text-blue-500" />
          </button>
          &nbsp;
          <button onclick={() => ondelete(recognition)} type="button">
            <Trash2 class="w-4 h-4 text-gray-400 hover:text-red-500" />
          </button>
        </div>
      {/if}
      <p class="text-lg text-gray-900 mb-3 mr-6">
        {#if recognition.sender_name}
          {recognition.sender_name}
        {:else}
          {recognition.sender_with_fallback}
        {/if}
        to <span class="font-bold">{recognition.recipient_fullnames}</span>
      </p>
      <p class="text-gray-700 ProseMirror">
        {@html recognition.comment}
      </p>
      <div class="flex flex-wrap items-center my-2 mt-3">
        {#if recognition.statements}
          {#each recognition.statements as statement (statement.id)}
            <span
              class="mx-1 my-1 flex items-center px-2 py-1 border rounded border-blue-100 bg-gray-50"
            >
              <img
                src={statement.symbol_url}
                class="h-6 mr-2"
                alt={statement.name}
              />
              <p class="text-xs">{statement.name}</p>
            </span>
          {/each}
        {/if}
      </div>
    </div>
  </div>
  <div class="w-full border-t border-gray-200">
    <div class="grid grid-cols-2 py-2 px-6 bg-gray-50 border-b border-gray-200">
      <div class="flex justify-start items-center">
        {#key recognition.reactions}
          {#each emojis as emoji}
            {#if hasEmojiReactions(emoji.name)}
              <img
                src={emoji.url}
                class="h-8 w-8 ml-3"
                data-name="reaction-icon"
                alt="Reaction {emoji.name}"
              />
              <div
                class="flex -space-x-2 overflow-hidden"
                data-name="reaction-users"
              >
                {#each reactionsByEmoji(emoji.name) as reaction}
                  <img
                    class="inline-block h-6 w-6 rounded-full ring-2 ring-white"
                    src={reaction.avatar_thumb_url ||
                      reaction.user.avatar_thumb_url}
                    alt="reaction"
                  />
                {/each}
              </div>
            {/if}
          {/each}
        {/key}
      </div>
      <div class="flex justify-end text-sm py-1">
        <button
          class="focus:outline-none"
          data-name="comments-button"
          onclick={() => (showComments = !showComments)}
        >
          {#if recognition.comments.length > 1}
            {recognition.comments.length} Comments
          {:else if recognition.comments.length === 1}
            1 Comment
          {:else}
            No Comments
          {/if}
        </button>
      </div>
    </div>

    <div
      class="flex w-full items-center justify-around md:justify-center py-2 px-8"
    >
      <Reaction
        reactionable={recognition}
        reaction={currentReaction}
        reactionable_type="Recognition"
        css="w-48 hover:bg-gray-100 h-full py-2"
        onupdate={(data) => updateReaction(data)}
        oncreate={(data) => {
          recognition.reactions = [...recognition.reactions, data]
          recognition = recognition
          currentReaction = data
        }}
        ondelete={(data) => removeReaction(data)}
      />
      <button
        class="flex justify-center items-center text-gray-900 hover:bg-gray-100 w-48 py-2"
        onclick={() => (showComments = !showComments)}
      >
        <MessageSquareText class="text-gray-800 h-5 w-5 mr-2" />
        Comments
      </button>
    </div>

    {#if showComments}
      <div class="py-6 px-12 bg-white border-t border-gray-100">
        <div class="mb-6 flex">
          <div class="flex space-x-3 flex-grow">
            <div class="flex-shrink-0">
              <div class="relative">
                <img
                  src={appStore.current_user.avatar_thumb_url}
                  class="mt-1 h-8 w-8 rounded-full bg-gray-400 flex items-center justify-center"
                  alt={appStore.current_user.name}
                />
              </div>
            </div>
            <div class="min-w-0 flex-1">
              <form action="#">
                <div>
                  <label for="comment" class="sr-only">Comment</label>

                  <Tiptap
                    placeholder="Add a comment"
                    content=""
                    allowEmojis={true}
                    allowGiphy={true}
                    buttons={[
                      ["bold", "italic", "underline", "strike"],
                      ["bulletList", "orderedList", "blockquote"],
                      ["link", "unlink"]
                    ]}
                    onready={(detail) => {
                      tiptapEditor = detail.editor
                    }}
                    onupdate={({ html }) => {
                      newComment = html
                      showCommentBtn = newComment !== "<p></p>"
                    }}
                  />
                </div>

                {#if showCommentBtn}
                  <div
                    class="mt-4 flex items-center justify-end space-x-4"
                    data-name="recognition-comment-button-cont"
                  >
                    <Button
                      class="btn btn-primary ml-2"
                      processing={savingComment}
                      onclick={createComment}
                    >
                      Add Comment
                    </Button>
                  </div>
                {/if}
              </form>
            </div>
          </div>
        </div>
        <div class="flow-root">
          <ul>
            {#each recognition.comments as comment (comment.id)}
              <RecognitionComment
                {comment}
                ondelete={(comment) => deleteComment(comment)}
              />
            {/each}
          </ul>
        </div>
      </div>
    {/if}
  </div>
</li>

<Modal bind:open={openEditModal} large={true}>
  <RecognitionDialog
    plan_id={appStore.plan.id}
    {recognition}
    edit={true}
    oncreated={() => (openEditModal = false)}
    onupdated={() => (openEditModal = false)}
  />
</Modal>
